/**
 * «Розклад інвертора: 6 вікон Deye»: a timeline of the day's windows with
 * draggable boundaries (step 1 hour, arrows on a focused boundary) and the
 * table of windows — cards on a phone. Elapsed windows are greyed and locked.
 * «Записати в інвертор» stores the edit for the rest of the day (`PUT
 * /windows`); «Повернути автоматичний» drops it. The draft lives in the
 * parent so the battery chart shows the windows being edited.
 */
import { type KeyboardEvent, type PointerEvent, useEffect, useRef, useState } from 'react';
import {
  DEYE_WINDOWS_PER_DAY,
  DEYE_WINDOW_MODES,
  type DeyeWindow,
  type DeyeWindowMode,
  type SiteWindowsResponse,
} from 'shared';

import { useResetWindows, useSaveWindows } from '../../hooks/use-site-results.js';
import { useLocale } from '../../i18n/locale-context.js';
import type { MessageKey } from '../../i18n/messages.js';
import { apiStatus, errorText } from '../errors.js';
import { fmtNumShort, fmtTime, parseNum } from '../format.js';
import {
  type WindowLimits,
  editProblem,
  hourAt,
  isElapsed,
  isRunning,
  moveStart,
  sameWindows,
  setMode,
  setPower,
  setSoc,
  startLocked,
  startOptions,
  valueProblem,
  windowEnd,
  windowMaxKw,
} from './edit.js';

/** Positions of the day's 6 windows: rows keep their place while starts move. */
const ROWS = Array.from({ length: DEYE_WINDOWS_PER_DAY }, (_, i) => i);

/** A number field that commits on blur or Enter (typing «0,5» passes through «0»). */
function NumCell({
  value,
  onCommit,
  disabled,
  label,
}: {
  value: number;
  onCommit: (v: number) => void;
  disabled: boolean;
  label: string;
}) {
  const { locale } = useLocale();
  const shown = fmtNumShort(locale, value, 1);
  const [text, setText] = useState(shown);
  useEffect(() => setText(shown), [shown]);
  // The field always shows the value applied: a clamped or refused entry
  // falls back to it (a changed value arrives through `shown`).
  const commit = () => {
    const v = parseNum(text);
    if (Number.isFinite(v)) onCommit(v);
    setText(shown);
  };
  return (
    <input
      inputMode="decimal"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
      }}
      aria-label={label}
      disabled={disabled}
    />
  );
}

export function WindowsEditor({
  siteId,
  server,
  draft,
  setDraft,
  lim,
  labels,
  now,
  tz,
  canWrite,
}: {
  siteId: string;
  server: SiteWindowsResponse & { windows: DeyeWindow[] };
  draft: readonly DeyeWindow[];
  setDraft: (ws: readonly DeyeWindow[]) => void;
  lim: WindowLimits;
  /** Wall-clock label of each position and of the day's end. */
  labels: readonly string[];
  /** «Now» in hours of the day, fractional. */
  now: number;
  tz: string;
  canWrite: boolean;
}) {
  const { t, tf, locale } = useLocale();
  const save = useSaveWindows(siteId);
  const reset = useResetWindows(siteId);
  const [msg, setMsg] = useState<string | null>(null);
  const [focusRow, setFocusRow] = useState<number | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const dragging = useRef<number | null>(null);
  const n = lim.hours;
  const dirty = !sameWindows(draft, server.windows);
  const pct = (h: number) => `${(h / n) * 100}%`;
  const label = (p: number) => labels[p] ?? '24:00';
  const modeName = (m: DeyeWindowMode) => t(`site.win.mode.${m}` as MessageKey);

  const edit = (next: readonly DeyeWindow[]) => {
    if (next === draft) return;
    setMsg(null);
    setDraft(next);
  };

  const onGripDown = (i: number) => (e: PointerEvent<HTMLSpanElement>) => {
    if (startLocked(draft, i, lim)) return;
    dragging.current = i;
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  };
  const onGripMove = (e: PointerEvent<HTMLSpanElement>) => {
    const i = dragging.current;
    const rect = trackRef.current?.getBoundingClientRect();
    if (i === null || !rect || rect.width === 0) return;
    edit(moveStart(draft, i, hourAt((e.clientX - rect.left) / rect.width, n), lim));
  };
  const onGripUp = () => {
    dragging.current = null;
  };
  const onGripKey = (i: number) => (e: KeyboardEvent<HTMLSpanElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const cur = draft[i]?.start_hour ?? 0;
    const opts = startOptions(draft, i, lim);
    const to = e.key === 'ArrowRight' ? opts.find((o) => o > cur) : [...opts].reverse().find((o) => o < cur);
    if (to !== undefined) edit(moveStart(draft, i, to, lim));
  };
  const focusWindow = (i: number) => {
    setFocusRow(i);
    const row = tableRef.current?.querySelector<HTMLTableRowElement>(`tr[data-row="${i}"]`);
    row?.querySelector<HTMLElement>('select:not(:disabled), input:not(:disabled)')?.focus();
  };

  const onSave = () => {
    const value = valueProblem(draft, lim);
    if (value) {
      setFocusRow(value.window);
      setMsg(
        value.kind === 'power'
          ? tf('site.win.err.power', { n: value.window + 1, max: fmtNumShort(locale, lim.pMaxKw) })
          : value.kind === 'grid'
            ? tf('site.win.err.grid', { n: value.window + 1, max: fmtNumShort(locale, value.max ?? 0) })
            : value.kind === 'no_grid'
              ? tf(lim.currentsByPhage ? 'site.win.err.no_grid_phage' : 'site.win.err.no_grid', {
                  n: value.window + 1,
                })
              : tf('site.win.err.soc', {
                  n: value.window + 1,
                  min: value.min ?? lim.socMinPct,
                  max: lim.socMaxPct,
                }),
      );
      return;
    }
    if (editProblem(server.windows, draft, lim)) {
      setMsg(t('site.win.err.invalid'));
      return;
    }
    save.mutate(
      { date: server.date, windows: [...draft] },
      {
        onSuccess: (r) => {
          if (r.windows) setDraft(r.windows);
          setMsg(t('site.win.saved'));
        },
        onError: (err) => {
          const s = apiStatus(err);
          setMsg(
            s === 409
              ? t('site.win.err.conflict')
              : s === 422
                ? t('site.win.err.invalid')
                : errorText(err, tf),
          );
        },
      },
    );
  };
  const onReset = () => {
    if (server.source !== 'manual') {
      setDraft(server.windows);
      setMsg(null);
      return;
    }
    reset.mutate(undefined, {
      onSuccess: (r) => {
        if (r.windows) setDraft(r.windows);
        setMsg(t('site.win.reset_done'));
      },
      onError: (err) => setMsg(apiStatus(err) === 409 ? t('site.win.err.conflict') : errorText(err, tf)),
    });
  };

  const status = dirty
    ? { text: t('site.win.status.draft'), manual: true }
    : server.source === 'manual'
      ? { text: t('site.win.status.manual'), manual: true }
      : {
          text: server.updated_at
            ? tf('site.win.status.auto', { time: fmtTime(server.updated_at, tz) })
            : t('site.win.status.auto_plain'),
          manual: false,
        };
  const sub = (w: DeyeWindow) =>
    w.mode === 'hold'
      ? t('site.win.sub.hold')
      : tf(`site.win.sub.${w.mode}` as MessageKey, { p: fmtNumShort(locale, w.power_kw), soc: w.soc_pct });

  return (
    <div>
      <div className="sched-head">
        <div className="chart-title">{t('site.win.title')}</div>
        <span className={`status-chip${status.manual ? ' manual' : ''}`}>{status.text}</span>
      </div>
      {/* The strip with draggable bounds — not on a phone: there the table only (owner, 22.10.2026). */}
      <div className="chart-wrap win-strip">
        <div className="tl">
          <div className="tl-track" ref={trackRef}>
            {draft.map((w, i) => {
              const end = windowEnd(draft, i, n);
              return (
                <button
                  key={`seg${i}-${w.start_hour}`}
                  type="button"
                  className={`tl-seg tl-${w.mode}`}
                  style={{ left: pct(w.start_hour), width: pct(end - w.start_hour) }}
                  aria-label={tf('site.win.aria.seg', {
                    n: i + 1,
                    mode: modeName(w.mode),
                    from: label(w.start_hour),
                    to: label(end),
                  })}
                  onClick={() => focusWindow(i)}
                >
                  <span className="tl-name">
                    <b>{i + 1}</b> · {modeName(w.mode)}
                  </span>
                  <span className="tl-sub">{sub(w)}</span>
                </button>
              );
            })}
            <span className="tl-past" style={{ width: pct(Math.min(now, n)) }} />
            <span className="tl-now" style={{ left: pct(Math.min(now, n)) }} />
            {draft.slice(1).map((w, k) => {
              const i = k + 1;
              const locked = startLocked(draft, i, lim) || !canWrite;
              return locked ? (
                <span key={`grip${i}`} className="tl-grip locked" style={{ left: pct(w.start_hour) }} />
              ) : (
                <span
                  key={`grip${i}`}
                  className="tl-grip"
                  style={{ left: pct(w.start_hour) }}
                  role="slider"
                  tabIndex={0}
                  aria-label={tf('site.win.aria.grip', { a: i, b: i + 1 })}
                  aria-valuemin={0}
                  aria-valuemax={n}
                  aria-valuenow={w.start_hour}
                  aria-valuetext={label(w.start_hour)}
                  onPointerDown={onGripDown(i)}
                  onPointerMove={onGripMove}
                  onPointerUp={onGripUp}
                  onPointerCancel={onGripUp}
                  onKeyDown={onGripKey(i)}
                />
              );
            })}
          </div>
          <div className="tl-ticks">
            {labels
              .map((l, p) => ({ l, p }))
              .filter(({ p }) => p % 3 === 0 && p < n)
              .map(({ l, p }) => (
                <span key={`tick${p}`} style={{ left: pct(p) }}>
                  {l}
                </span>
              ))}
          </div>
        </div>
      </div>
      <p className="hint win-strip">{t('site.win.hint')}</p>
      <p className="hint win-phone">{t('site.win.hint_phone')}</p>
      <div className="table-wrap">
        {/* Phone: two lines a window, the column names once (owner, 22.10.2026). */}
        <div className="wins-head" aria-hidden="true">
          <span>{t('site.win.col.mode')}</span>
          <span>{t('site.win.col.power_short')}</span>
          <span>{t('site.win.col.soc_short')}</span>
        </div>
        <table className="wins" ref={tableRef}>
          <thead>
            <tr>
              <th>{t('site.win.col.window')}</th>
              <th>{t('site.win.col.time')}</th>
              <th>{t('site.win.col.mode')}</th>
              <th>{t('site.win.col.power')}</th>
              <th>{t('site.win.col.soc')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {ROWS.map((i) => {
              const w = draft[i];
              if (!w) return null;
              const end = windowEnd(draft, i, n);
              const past = isElapsed(draft, i, lim);
              const off = past || !canWrite;
              const opts = startOptions(draft, i, lim);
              const classes = [past ? 'past' : '', focusRow === i ? 'focus' : ''].filter(Boolean).join(' ');
              return (
                <tr key={`row${i}`} data-row={i} className={classes || undefined}>
                  <td>
                    <span className={`idx idx-${w.mode}`}>{i + 1}</span>
                  </td>
                  <td>
                    {opts.length > 0 && canWrite ? (
                      <select
                        value={w.start_hour}
                        aria-label={tf('site.win.aria.start', { n: i + 1 })}
                        onChange={(e) => edit(moveStart(draft, i, Number(e.target.value), lim))}
                      >
                        {opts.map((h) => (
                          <option key={h} value={h}>
                            {label(h)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="num">{label(w.start_hour)}</span>
                    )}{' '}
                    – <span className="num">{label(end)}</span>
                  </td>
                  <td>
                    <select
                      value={w.mode}
                      disabled={off}
                      aria-label={tf('site.win.aria.mode', { n: i + 1 })}
                      onChange={(e) => edit(setMode(draft, i, e.target.value as DeyeWindowMode, lim))}
                    >
                      {DEYE_WINDOW_MODES.map((m) => (
                        <option
                          key={m}
                          value={m}
                          disabled={m === 'grid_charge' && w.mode !== m && windowMaxKw(draft, i, m, lim) <= 0}
                        >
                          {modeName(m)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <span className="cell-label">{t('site.win.col.power')}</span>
                    <NumCell
                      value={w.power_kw}
                      onCommit={(v) => edit(setPower(draft, i, v, lim))}
                      disabled={off || w.mode === 'hold'}
                      label={tf('site.win.aria.power', { n: i + 1 })}
                    />
                  </td>
                  <td>
                    <span className="cell-label">{t('site.win.col.soc')}</span>
                    {w.mode === 'hold' ? (
                      <span className="sub">
                        <span className="soc-hold">{t('site.win.soc.hold')}</span>
                        {/* Phone: the mode already says it; the narrow column a dash. */}
                        <span className="soc-dash" aria-hidden="true">
                          —
                        </span>
                      </span>
                    ) : (
                      <>
                        <span className="sub soc-word">
                          {t(w.mode === 'grid_charge' ? 'site.win.soc.up_to' : 'site.win.soc.not_below')}
                        </span>{' '}
                        <NumCell
                          value={w.soc_pct}
                          onCommit={(v) => edit(setSoc(draft, i, v, lim))}
                          disabled={off}
                          label={tf('site.win.aria.soc', { n: i + 1 })}
                        />
                      </>
                    )}
                  </td>
                  <td className="sub">
                    {past
                      ? t('site.win.state.past')
                      : isRunning(draft, i, lim)
                        ? t('site.win.state.running')
                        : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="row-actions">
        <button
          type="button"
          className="btn primary"
          onClick={onSave}
          disabled={!dirty || !canWrite || save.isPending}
        >
          {t('site.win.save')}
        </button>
        <button
          type="button"
          className="btn"
          onClick={onReset}
          disabled={(!dirty && server.source !== 'manual') || reset.isPending || (!canWrite && !dirty)}
        >
          {t('site.win.auto')}
        </button>
        <span className="sub" aria-live="polite">
          {!canWrite ? t('site.win.err.no_access') : msg}
        </span>
      </div>
    </div>
  );
}
